import type { PriceImportMatchTier } from 'apis/price-imports';

/**
 * How the match is presented, and how much it should be trusted at a glance.
 *
 * Tiers 0 and 1 (supplier code, prior link, natural key) are exact by
 * construction. The fuzzy tier only ever auto-matches when the family name
 * scored at or above the shared suggestion threshold AND exactly one variant
 * survived the identity gates, so it is trustworthy but worth a look.
 */
export interface MatchTierPresentation {
  label: string;
  colorScheme: string;
  /** One line explaining why the row landed where it did. */
  hint: string;
}

export const MATCH_TIER_PRESENTATION: Record<PriceImportMatchTier, MatchTierPresentation> = {
  supplier_code: {
    label: 'SKU',
    colorScheme: 'green',
    hint: "Matched on this supplier's own product code.",
  },
  prior_link: {
    label: 'Remembered',
    colorScheme: 'green',
    hint: 'This line resolved to the same product in an earlier committed import.',
  },
  natural_key: {
    label: 'Exact',
    colorScheme: 'green',
    hint: 'Every identifying field matched an existing product exactly.',
  },
  fuzzy_family: {
    label: 'Matched',
    colorScheme: 'teal',
    hint: 'The family name matched and exactly one variant fits this row.',
  },
  proposed: {
    label: 'New',
    colorScheme: 'purple',
    hint: 'Nothing in the catalogue fits. Committing would create it.',
  },
  none: {
    label: 'Unmatched',
    colorScheme: 'orange',
    hint: 'Needs a product picked before it can be priced.',
  },
};

export function tierPresentation(tier: PriceImportMatchTier | null): MatchTierPresentation {
  return MATCH_TIER_PRESENTATION[tier ?? 'none'] ?? MATCH_TIER_PRESENTATION.none;
}

/** Tiers safe to bulk-accept without opening each row. */
export const HIGH_CONFIDENCE_TIERS: PriceImportMatchTier[] = ['supplier_code', 'prior_link', 'natural_key'];

export function isHighConfidence(tier: PriceImportMatchTier | null): boolean {
  return tier != null && HIGH_CONFIDENCE_TIERS.includes(tier);
}
